
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Info, Heart, Users, Award, Clock } from "lucide-react";

const AboutUs = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-white to-slate-50 py-20 md:py-28">
          <div className="absolute inset-0 z-0">
            <div className="absolute top-0 left-0 right-0 h-full w-full bg-repeat" 
                style={{ 
                  backgroundImage: `url('https://flowcare.health/lovable-uploads/000cfbcc-abc0-4c33-8279-70b0622a206c.png')`, 
                  backgroundSize: '200px',
                  opacity: 0.08
                }} 
            />
          </div>
          
          <div className="container mx-auto px-6 relative z-10">
            <div className="flex flex-col items-center text-center mb-12">
              <div className="inline-flex items-center gap-2 bg-abbella-aqua/10 text-abbella-aqua px-4 py-1.5 rounded-full mb-6 border border-abbella-aqua/20">
                <Info size={16} strokeWidth={1.5} className="text-abbella-aqua" />
                <span className="text-sm font-medium">Our Story</span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-abbella-ebony mb-6">About FlowCare</h1>
              <p className="text-lg text-slate-700 max-w-2xl">Discover our mission to transform home care with compassionate, flexible services that prioritize both quality and affordability.</p>
            </div>
          </div>
        </section>

        {/* Our Mission Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-abbella-ebony mb-6">Our Mission & Values</h2>
                <p className="text-slate-700 mb-6">At FlowCare, we believe quality home care should be accessible to everyone. Our mission is to provide exceptional, personalized care services that enable individuals to maintain their independence and dignity in the comfort of their own homes.</p>
                
                <div className="space-y-4 mt-8">
                  {[
                    { icon: <Heart className="text-abbella-aqua" />, title: "Compassion", description: "We approach every client with genuine care and empathy" },
                    { icon: <Award className="text-abbella-aqua" />, title: "Excellence", description: "We hold ourselves to the highest standards of care" },
                    { icon: <Clock className="text-abbella-aqua" />, title: "Flexibility", description: "We adapt our services to meet your unique needs" },
                    { icon: <Users className="text-abbella-aqua" />, title: "Dignity", description: "We respect the independence and choices of every client" }
                  ].map((value, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="p-2 bg-abbella-aqua/10 rounded-full">{value.icon}</div>
                      <div>
                        <h3 className="font-medium text-abbella-ebony">{value.title}</h3>
                        <p className="text-slate-600 text-sm">{value.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="rounded-xl overflow-hidden shadow-xl">
                  <img 
                    src="/lovable-uploads/c49672aa-43e3-4649-b74f-4065ae7a97c6.png" 
                    alt="Caregivers providing compassionate service" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Our Story Section */}
        <section className="py-16 bg-slate-50">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-3xl font-bold text-abbella-ebony mb-6 text-center">Our Story</h2>
              <div className="prose prose-lg max-w-none text-slate-700">
                <p>FlowCare was founded in response to a critical gap we observed in the home care industry. Many families struggled to find reliable, quality care that was also flexible enough to meet their unique needs.</p>
                
                <p className="mt-4">Our founder, after experiencing the challenges of arranging care for a loved one, set out to create a service that would truly prioritize both caregivers and the individuals they serve. We believed that by treating our care professionals with respect and providing them with competitive compensation, we could build a team of dedicated professionals who deliver exceptional care.</p>
                
                <p className="mt-4">Since our inception, we've grown to serve the Baltimore area and surrounding counties in Maryland, becoming a trusted partner for families seeking compassionate home care solutions.</p>
              </div>
            </div>
          </div>
        </section>
        
        {/* Call to Action */}
        <section className="py-16 bg-abbella-aqua/10">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl font-bold text-abbella-ebony mb-4">Ready to experience the FlowCare difference?</h2>
            <p className="text-lg text-slate-700 mb-8 max-w-2xl mx-auto">Let us show you how our compassionate, flexible approach to care can make a difference in your life or the life of your loved one.</p>
            <Button className="bg-abbella-aqua hover:bg-abbella-aqua/90 text-white px-8 py-6 text-lg rounded-lg">
              Contact Us Today
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default AboutUs;
